import {
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import {
  PanelResizeHandle,
  type ImperativePanelGroupHandle,
} from "react-resizable-panels";

export type ResizeHandleProps = {
  /** Unique id for the handle; surfaced as data-testid + aria-controls anchor. */
  id: string;
  /** Pane group ref, so ESC during drag can revert to the pre-drag layout and double-click can reset. */
  panelGroupRef: RefObject<ImperativePanelGroupHandle | null>;
  /** Default layout to snap back to on double-click reset. */
  defaultLayout: number[];
  /**
   * Stable signal that should abort an in-progress drag (e.g., the chat
   * route is switching tickets). When this value changes mid-drag, we
   * dispatch a synthetic pointerup so react-resizable-panels finishes the
   * drag cleanly instead of leaving the body cursor/no-select state stuck.
   */
  abortSignal?: string | number | null;
  /**
   * Optional callback so the parent route can suppress competing CSS
   * transitions while a drag is in flight. Receives true on drag-start,
   * false on drag-end / ESC-cancel / unmount.
   */
  onDraggingChange?: (dragging: boolean) => void;
};

const KEYBOARD_HINT_TEXT =
  "Drag to resize. Press Escape to cancel, double-click to reset.";

export function ResizeHandle({
  id,
  panelGroupRef,
  defaultLayout,
  abortSignal,
  onDraggingChange,
}: ResizeHandleProps) {
  const [isDragging, setIsDragging] = useState(false);
  const preDragLayoutRef = useRef<number[] | null>(null);
  const hintId = useId();

  // Capture the layout at drag start so ESC can revert.
  const handleDragging = useCallback(
    (dragging: boolean) => {
      setIsDragging(dragging);
      onDraggingChange?.(dragging);
      if (dragging) {
        preDragLayoutRef.current = panelGroupRef.current?.getLayout() ?? null;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
      } else {
        preDragLayoutRef.current = null;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      }
    },
    [onDraggingChange, panelGroupRef],
  );

  // ESC during drag → revert.
  useEffect(() => {
    if (!isDragging) return;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const pre = preDragLayoutRef.current;
      if (pre && panelGroupRef.current) {
        panelGroupRef.current.setLayout(pre);
      }
      // Synthesize pointerup so react-resizable-panels exits its drag state cleanly.
      window.dispatchEvent(
        new PointerEvent("pointerup", { bubbles: true, cancelable: true }),
      );
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDragging, panelGroupRef]);

  // Abort signal change mid-drag (ticket switch, route change) → end the drag.
  useEffect(() => {
    if (!isDragging) return;
    window.dispatchEvent(
      new PointerEvent("pointerup", { bubbles: true, cancelable: true }),
    );
    // intentionally only depend on the abort signal — we want this to fire
    // when the signal changes, not when isDragging flips back to false.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [abortSignal]);

  // Clean up body styles if the handle unmounts mid-drag (e.g., layout flips
  // 3-pane → chat-only because the ticket was closed).
  useEffect(() => {
    return () => {
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, []);

  const handleDoubleClick = useCallback(() => {
    panelGroupRef.current?.setLayout(defaultLayout);
  }, [defaultLayout, panelGroupRef]);

  return (
    <PanelResizeHandle
      id={id}
      onDragging={handleDragging}
      hitAreaMargins={{ coarse: 24, fine: 12 }}
      className="group relative flex items-center justify-center"
      data-testid={`askpuntt-resize-handle-${id}`}
      aria-label={
        id === "chat-right"
          ? "Resize chat and right pane"
          : id === "context-artwork"
            ? "Resize context and artwork panes"
            : `Resize ${id} panes`
      }
      aria-describedby={hintId}
      onDoubleClick={handleDoubleClick}
    >
      {/* Persistent 1px divider at rest; expands to 4px band on hover/focus/drag */}
      <div
        className={`pointer-events-none h-full transition-[width,background-color] duration-150 ${
          isDragging
            ? "w-1 bg-puntt-accent-10"
            : "w-px bg-puntt-neutral-gray-6 group-hover:w-1 group-hover:bg-puntt-neutral-gray-8 group-focus-visible:w-1 group-focus-visible:bg-puntt-accent-9"
        }`}
        aria-hidden="true"
        data-testid={`askpuntt-resize-handle-divider-${id}`}
      />
      {/* Grip rail — visible only on hover or focus-visible */}
      <div
        className={`pointer-events-none absolute left-1/2 top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-puntt-neutral-gray-9 opacity-0 transition-opacity duration-150 ${
          isDragging
            ? "opacity-100"
            : "group-hover:opacity-100 group-focus-visible:opacity-100"
        }`}
        aria-hidden="true"
        data-testid={`askpuntt-resize-handle-grip-${id}`}
      />
      <span id={hintId} className="sr-only">
        {KEYBOARD_HINT_TEXT}
      </span>
    </PanelResizeHandle>
  );
}
